import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Save, Search } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { db } from "@/lib/billing";

type RolePermission = {
  id: string;
  role: string;
  page_path: string;
  can_view: boolean;
  can_create: boolean;
  can_edit: boolean;
  can_delete: boolean;
  can_manage_finance: boolean;
  notes: string | null;
};

async function fetchPermissions(): Promise<RolePermission[]> {
  const { data, error } = await db.from("role_permissions").select("*").order("role").order("page_path");
  if (error) throw error;
  return data ?? [];
}

export default function AdminRolesPermissionsPage() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const { data: permissions = [], isLoading } = useQuery({ queryKey: ["role-permissions"], queryFn: fetchPermissions });
  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return permissions.filter((item) => [item.role, item.page_path, item.notes].join(" ").toLowerCase().includes(q));
  }, [permissions, search]);

  const save = useMutation({
    mutationFn: async ({ id, patch }: { id: string; patch: Partial<RolePermission> }) => {
      const { error } = await db.from("role_permissions").update(patch).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Role permission updated");
      queryClient.invalidateQueries({ queryKey: ["role-permissions"] });
    },
    onError: (err: any) => toast.error(err?.message ?? "Could not update permission"),
  });

  const Toggle = ({ item, field }: { item: RolePermission; field: keyof Pick<RolePermission, "can_view" | "can_create" | "can_edit" | "can_delete" | "can_manage_finance"> }) => (
    <Switch checked={Boolean(item[field])} onCheckedChange={(checked) => save.mutate({ id: item.id, patch: { [field]: checked } })} />
  );

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-30 border-b border-border bg-background/80 px-6 py-4 backdrop-blur-xl">
        <h1 className="text-lg font-semibold text-foreground">Roles & Permissions</h1>
        <p className="text-xs text-muted-foreground">Role/page visibility and financial action matrix.</p>
      </header>
      <main className="p-6">
        <Card>
          <CardHeader className="gap-3">
            <CardTitle className="text-base">Permission matrix</CardTitle>
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input className="pl-9" placeholder="Search role or page..." value={search} onChange={(event) => setSearch(event.target.value)} />
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? <div className="flex h-48 items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div> : (
              <Table>
                <TableHeader><TableRow><TableHead>Role</TableHead><TableHead>Page</TableHead><TableHead>View</TableHead><TableHead>Create</TableHead><TableHead>Edit</TableHead><TableHead>Delete</TableHead><TableHead>Finance</TableHead><TableHead>Notes</TableHead></TableRow></TableHeader>
                <TableBody>
                  {filtered.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell className="font-medium">{item.role}</TableCell>
                      <TableCell>{item.page_path}</TableCell>
                      <TableCell><Toggle item={item} field="can_view" /></TableCell>
                      <TableCell><Toggle item={item} field="can_create" /></TableCell>
                      <TableCell><Toggle item={item} field="can_edit" /></TableCell>
                      <TableCell><Toggle item={item} field="can_delete" /></TableCell>
                      <TableCell><Toggle item={item} field="can_manage_finance" /></TableCell>
                      <TableCell className="max-w-[220px] truncate text-xs text-muted-foreground">{item.notes}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            {save.isPending && <div className="mt-3 flex items-center gap-2 text-xs text-muted-foreground"><Save className="h-3 w-3" />Saving...</div>}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
